import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
  mergeStyleSets,
} from '@fluentui/react';
import { Buildings, GlobeHemisphereWest, MapPin, ShieldCheck, Info } from '@phosphor-icons/react';
import { MapContainer, TileLayer, GeoJSON, ScaleControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { boroondaraPalette } from '../theme/boroondaraTheme';
import { accessService } from '../services/accessService';

/* ── Geospatial sub-components ── */
import { useMapLayers } from '../components/geospatial/hooks/useMapLayers';
import { BASEMAPS, BasemapKey, DEFAULT_CENTER, DEFAULT_ZOOM } from '../components/geospatial/constants';
import { ChoroplethMetricDef, ChoroplethResponse, GeoJSONFeature, GeoChatMessage, GeoMapAction } from '../components/geospatial/types';
import { geoChatService } from '../services/geoChatService';
import GeoChat from '../components/geospatial/GeoChat';
import LayerPanel from '../components/geospatial/LayerPanel';
import FeatureDetail from '../components/geospatial/FeatureDetail';
import ClusteredLayer from '../components/geospatial/ClusteredLayer';
import MapToolbar from '../components/geospatial/MapToolbar';
import SearchBar from '../components/geospatial/SearchBar';
import ChoroplethLayer from '../components/geospatial/ChoroplethLayer';
import ChoroplethControls from '../components/geospatial/ChoroplethControls';
import ChoroplethLegend from '../components/geospatial/ChoroplethLegend';
import SA2DetailPanel from '../components/geospatial/SA2DetailPanel';
import ExportTools from '../components/geospatial/ExportTools';

/* ── Fix Leaflet marker icon paths for webpack ── */
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const p = boroondaraPalette;

/* ── Styles ── */
const styles = mergeStyleSets({
  page: {
    minHeight: 'calc(100vh - 52px)', background: p.bg,
  } as any,
  flexRow: {
    display: 'flex', minHeight: 'calc(100vh - 52px)',
  } as any,
  mainContent: {
    flex: 1, minWidth: 0, padding: 32,
    '@media (max-width: 768px)': { padding: 16 },
  } as any,
  container: { margin: '0 auto' },
  headerRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: 20, flexWrap: 'wrap', gap: 12,
    '@media (max-width: 768px)': { flexDirection: 'column' },
  } as any,
  headerLeft: { flex: 1, minWidth: 0 },
  title: {
    color: p.text, fontWeight: 800, fontSize: 28, letterSpacing: '-0.02em', marginBottom: 4,
    '@media (max-width: 768px)': { fontSize: 22 },
  } as any,
  subtitle: { color: p.text2, fontSize: 13, lineHeight: '20px', maxWidth: 720, marginBottom: 10 },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 6 } as any,
  chip: {
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 999,
    background: p.bg2, border: `1px solid ${p.borderSoft}`, color: p.text2, fontSize: 11, fontWeight: 500,
  },
  searchWrap: {
    width: 280, flexShrink: 0,
    '@media (max-width: 768px)': { width: '100%' },
  } as any,
  /* Map */
  mapCard: {
    borderRadius: 4, border: `1px solid ${p.border}`,
    overflow: 'hidden', marginBottom: 16, background: '#FFFFFF',
    position: 'relative',
  } as any,
  mapWrap: { width: '100%', height: 'calc(100vh - 340px)', minHeight: 480, position: 'relative' } as any,
  basemapTabs: {
    position: 'absolute' as any, top: 12, left: 12, zIndex: 1000,
    display: 'flex', gap: 2, background: 'rgba(255,255,255,0.92)',
    borderRadius: 4, boxShadow: '0 1px 4px rgba(0,0,0,0.15)', overflow: 'hidden',
  } as any,
  basemapBtn: {
    padding: '5px 10px', border: 'none', background: 'transparent',
    cursor: 'pointer', fontSize: 11, fontWeight: 500, color: p.text2,
    selectors: { ':hover': { background: p.bg2 } },
  } as any,
  infoBar: {
    display: 'flex', alignItems: 'center', gap: 12, padding: 14, borderRadius: 4,
    background: 'rgba(0,120,212,0.04)', border: '1px solid rgba(0,120,212,0.12)',
  },
  centered: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    minHeight: 400, gap: 16,
  } as any,
});

/* ── Component ── */
export default function Geospatial() {
  /* Map refs (declared before useMapLayers so the ref can be passed in) */
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);

  const {
    layers, stats, activeLayers, featureData, selectedFeature,
    loading, error, toggleLayer, selectFeature,
    activeFilters, setActiveFilter, clearFilter, clearAll,
  } = useMapLayers(mapInstanceRef);

  /* Basemap */
  const [basemap, setBasemap] = useState<BasemapKey>('Street');

  /* Choropleth state */
  const [choroplethEnabled, setChoroplethEnabled] = useState(false);
  const [choroplethTopics, setChoroplethTopics] = useState<Record<string, ChoroplethMetricDef[]>>({});
  const [choroplethTopic, setChoroplethTopic] = useState('demographics');
  const [choroplethMetric, setChoroplethMetric] = useState('population_total');
  const [choroplethYear, setChoroplethYear] = useState(2021);
  const [choroplethData, setChoroplethData] = useState<ChoroplethResponse | null>(null);

  /* SA2 detail */
  const [selectedSA2, setSelectedSA2] = useState<{ sa2_id: string; sa2_name: string } | null>(null);

  /* AI Chat state */
  const [chatMessages, setChatMessages] = useState<GeoChatMessage[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSuggestions, setChatSuggestions] = useState<string[]>([]);
  const [chatCollapsed, setChatCollapsed] = useState(false);

  /* Load choropleth metrics on mount */
  useEffect(() => {
    accessService
      .get<Record<string, ChoroplethMetricDef[]>>('/spatial/choropleth/metrics')
      .then((res) => setChoroplethTopics(res.data))
      .catch(() => {});
  }, []);

  /* Load choropleth data when params change */
  useEffect(() => {
    if (!choroplethEnabled) { setChoroplethData(null); return; }
    accessService
      .get<ChoroplethResponse>(
        `/spatial/choropleth?topic=${choroplethTopic}&metric=${choroplethMetric}&year=${choroplethYear}`
      )
      .then((res) => setChoroplethData(res.data))
      .catch(() => setChoroplethData(null));
  }, [choroplethEnabled, choroplethTopic, choroplethMetric, choroplethYear]);

  const handleTopicChange = useCallback(
    (topic: string) => {
      setChoroplethTopic(topic);
      const metrics = choroplethTopics[topic];
      if (metrics?.length) setChoroplethMetric(metrics[0].key);
    },
    [choroplethTopics]
  );

  const handleSearchSelect = useCallback((feature: GeoJSONFeature, _layerColor: string) => {
    if (!feature.geometry) return;
    const map = mapInstanceRef.current;
    if (!map) return;
    if (feature.geometry.type === 'Point') {
      const [lng, lat] = feature.geometry.coordinates;
      map.setView([lat, lng], 16);
    } else if (feature.geometry.type === 'Polygon') {
      const coords = feature.geometry.coordinates[0].map(([lng, lat]: number[]) => L.latLng(lat, lng));
      map.fitBounds(L.latLngBounds(coords), { padding: [30, 30] });
    }
    selectFeature(feature.properties);
  }, [selectFeature]);

  const getFilteredData = useCallback(
    (layerId: string, layerCode: string) => {
      const fc = featureData[layerId];
      if (!fc?.features) return fc;
      let filtered = fc.features;
      const chipFilter = activeFilters[layerCode];
      if (chipFilter) {
        filtered = filtered.filter((f) => {
          const props = f.properties || {};
          return Object.values(props).some((v) => typeof v === 'string' && v === chipFilter);
        });
      }
      if (filtered.length === fc.features.length) return fc;
      return { ...fc, features: filtered };
    },
    [featureData, activeFilters]
  );

  /* ── AI Chat: dispatch map actions ── */
  const dispatchMapAction = useCallback((mapAction?: GeoMapAction) => {
    if (!mapAction) return;
    const { action } = mapAction;

    // Toggle layers on
    if (mapAction.layerIdsOn) {
      for (const lid of mapAction.layerIdsOn) {
        if (!activeLayers.has(lid)) toggleLayer(lid);
      }
    }
    // Toggle layers off
    if (mapAction.layerIdsOff) {
      for (const lid of mapAction.layerIdsOff) {
        if (activeLayers.has(lid)) toggleLayer(lid);
      }
    }

    // Set filter (also ensure layer is on)
    if (action === 'set_filter' && mapAction.filter) {
      const { layerCode, filterValue } = mapAction.filter;
      // Enable the layer if not active
      const layer = layers.find(l => l.code === layerCode);
      if (layer && !activeLayers.has(layer.layer_id)) toggleLayer(layer.layer_id);
      setActiveFilter(layerCode, filterValue);
    }

    // Clear filter
    if (action === 'clear_filter' && mapAction.filter) {
      clearFilter(mapAction.filter.layerCode);
    }

    // Enable choropleth
    if (action === 'enable_choropleth' && mapAction.choropleth) {
      const { topic, metric, year } = mapAction.choropleth;
      setChoroplethTopic(topic);
      setChoroplethMetric(metric);
      setChoroplethYear(year);
      setChoroplethEnabled(true);
    }

    // Disable choropleth
    if (action === 'disable_choropleth') {
      setChoroplethEnabled(false);
    }

    // Change basemap
    if (action === 'change_basemap' && mapAction.basemap) {
      const key = mapAction.basemap as BasemapKey;
      if (key in BASEMAPS) setBasemap(key);
    }

    // Zoom to location
    if (action === 'zoom_to' && mapAction.zoomTo) {
      const map = mapInstanceRef.current;
      if (map) {
        map.setView([mapAction.zoomTo.lat, mapAction.zoomTo.lng], mapAction.zoomTo.zoom || 15);
      }
    }
  }, [activeLayers, layers, toggleLayer, setActiveFilter, clearFilter]);

  /* ── AI Chat: send message ── */
  const handleChatSend = useCallback(async (text: string) => {
    const userMsg: GeoChatMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };
    setChatMessages(prev => [...prev, userMsg]);
    setChatLoading(true);

    try {
      const activeLayerCodes = layers
        .filter(l => activeLayers.has(l.layer_id))
        .map(l => l.code);

      const response = await geoChatService.query({
        query: text,
        activeLayers: activeLayerCodes,
        activeFilters,
        choroplethEnabled,
        choroplethTopic,
        choroplethMetric,
        choroplethYear,
        basemap,
      });

      dispatchMapAction(response.mapAction);

      const assistantMsg: GeoChatMessage = {
        id: `a-${Date.now()}`,
        role: 'assistant',
        content: response.message,
        timestamp: new Date().toISOString(),
        suggestions: response.suggestions,
        mapAction: response.mapAction,
      };
      setChatMessages(prev => [...prev, assistantMsg]);
      setChatSuggestions(response.suggestions || []);
    } catch (err: any) {
      const errMsg: GeoChatMessage = {
        id: `e-${Date.now()}`,
        role: 'assistant',
        content: 'Sorry, I couldn\'t process that request. Please try again.',
        timestamp: new Date().toISOString(),
        suggestions: ['Show me all parks', 'What layers are available?'],
      };
      setChatMessages(prev => [...prev, errMsg]);
    } finally {
      setChatLoading(false);
    }
  }, [layers, activeLayers, activeFilters, choroplethEnabled, choroplethTopic, choroplethMetric, choroplethYear, basemap, dispatchMapAction]);

  /* ── Loading ── */
  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.centered}>
          <Spinner size={SpinnerSize.large} label="Loading spatial data..." />
        </div>
      </div>
    );
  }

  /* ── Error ── */
  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>{error}</MessageBar>
        </div>
      </div>
    );
  }

  const basemapCfg = BASEMAPS[basemap];

  return (
    <div className={styles.page}>
      <div className={styles.flexRow}>
      <div className={styles.mainContent}>
      <div className={styles.container}>
        {/* ── Header row with search ── */}
        <div className={styles.headerRow}>
          <div className={styles.headerLeft}>
            <div className={styles.title}>Spatial Data Explorer</div>
            <div className={styles.subtitle}>
              Toggle layers, search features, explore ABS Census choropleth maps, and export data.
            </div>
            <div className={styles.chipRow}>
              <span className={styles.chip}><Buildings size={11} weight="bold" /> Boroondara</span>
              <span className={styles.chip}><GlobeHemisphereWest size={11} weight="bold" /> {stats?.totalLayers || 0} Layers</span>
              <span className={styles.chip}><MapPin size={11} weight="bold" /> {stats?.totalFeatures?.toLocaleString() || 0} Features</span>
              <span className={styles.chip}><ShieldCheck size={11} weight="bold" /> PostGIS</span>
            </div>
          </div>
          <div className={styles.searchWrap}>
            <SearchBar
              layers={layers}
              featureData={featureData}
              activeLayers={activeLayers}
              onFeatureSelect={handleSearchSelect}
            />
          </div>
        </div>

        {/* ── Layer toggles (horizontal above map) ── */}
        <LayerPanel
          layers={layers}
          activeLayers={activeLayers}
          onToggle={toggleLayer}
          choroplethEnabled={choroplethEnabled}
          onToggleChoropleth={() => setChoroplethEnabled((v) => !v)}
          activeFilters={activeFilters}
          onFilterClick={setActiveFilter}
        />

        {/* ── Choropleth controls (shown when census enabled) ── */}
        {choroplethEnabled && (
          <ChoroplethControls
            topics={choroplethTopics}
            selectedTopic={choroplethTopic}
            selectedMetric={choroplethMetric}
            selectedYear={choroplethYear}
            onTopicChange={handleTopicChange}
            onMetricChange={setChoroplethMetric}
            onYearChange={setChoroplethYear}
          />
        )}

        {/* ── Full-width map ── */}
        <div className={`${styles.mapCard} mapSection`}>
          <div className={styles.mapWrap} ref={mapRef}>
            <MapContainer
              center={DEFAULT_CENTER}
              zoom={DEFAULT_ZOOM}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
              ref={(ref) => { if (ref) mapInstanceRef.current = ref; }}
            >
              <TileLayer key={basemap} attribution={basemapCfg.attribution} url={basemapCfg.url} />
              <ScaleControl position="bottomleft" />
              <MapToolbar featureData={featureData} activeLayers={activeLayers} />

              {/* Polygon layers */}
              {layers
                .filter((l) => activeLayers.has(l.layer_id) && l.layer_type === 'polygon' && featureData[l.layer_id])
                .map((layer) => {
                  const data = getFilteredData(layer.layer_id, layer.code);
                  if (!data?.features?.length) return null;
                  return (
                    <GeoJSON
                      key={`${layer.layer_id}-${data.features.length}`}
                      data={data as any}
                      style={() => ({
                        fillColor: layer.color, color: layer.color, weight: 2, fillOpacity: 0.15,
                      })}
                      onEachFeature={(feature: any, leafletLayer: L.Layer) => {
                        const props = feature.properties || {};
                        leafletLayer.bindTooltip(
                          `<strong>${props.name || 'Feature'}</strong><br/><span style="color:#747474;font-size:11px">${layer.name}</span>`,
                          { direction: 'top', sticky: true }
                        );
                        leafletLayer.bindPopup(
                          `<div style="font-family:Roboto,Segoe UI,sans-serif;min-width:180px">` +
                          `<div style="border-left:3px solid ${layer.color};padding-left:8px;margin-bottom:6px">` +
                          `<div style="font-weight:700;font-size:14px">${props.name || 'Feature'}</div>` +
                          `<div style="font-size:11px;color:#747474">${props.layer_name || ''}</div></div>` +
                          `<div style="font-size:12px;color:#484848">${props.description || ''}</div></div>`
                        );
                        leafletLayer.on('mouseover', (e: any) => { e.target.setStyle({ fillOpacity: 0.35, weight: 3 }); });
                        leafletLayer.on('mouseout', (e: any) => { e.target.setStyle({ fillOpacity: 0.15, weight: 2 }); });
                        leafletLayer.on('click', () => selectFeature(props));
                      }}
                    />
                  );
                })}

              {/* Point layers with clustering */}
              {layers
                .filter((l) => activeLayers.has(l.layer_id) && l.layer_type === 'point' && featureData[l.layer_id])
                .map((layer) => {
                  const data = getFilteredData(layer.layer_id, layer.code);
                  if (!data?.features?.length) return null;
                  return (
                    <ClusteredLayer
                      key={`${layer.layer_id}-${data.features.length}`}
                      layer={layer}
                      data={data as any}
                      onFeatureClick={selectFeature}
                    />
                  );
                })}

              {/* Choropleth */}
              {choroplethEnabled && (
                <ChoroplethLayer
                  data={choroplethData}
                  onSA2Click={(props) => setSelectedSA2({ sa2_id: props.sa2_id, sa2_name: props.sa2_name })}
                />
              )}
              {choroplethEnabled && choroplethData?.meta && (
                <ChoroplethLegend meta={choroplethData.meta} />
              )}
            </MapContainer>

            {/* Basemap tabs */}
            <div className={styles.basemapTabs}>
              {(Object.keys(BASEMAPS) as BasemapKey[]).map((key) => (
                <button
                  key={key}
                  className={styles.basemapBtn}
                  style={basemap === key ? { background: p.primary, color: '#fff' } : undefined}
                  onClick={() => setBasemap(key)}
                >
                  {key}
                </button>
              ))}
            </div>

            {/* Export tools */}
            <ExportTools
              layers={layers}
              activeLayers={activeLayers}
              featureData={featureData}
              mapContainerRef={mapRef}
            />
          </div>
        </div>

        {/* ── SA2 Census Detail ── */}
        {selectedSA2 && (
          <SA2DetailPanel
            sa2Id={selectedSA2.sa2_id}
            sa2Name={selectedSA2.sa2_name}
            year={choroplethYear}
            onClose={() => setSelectedSA2(null)}
          />
        )}

        {/* ── Feature Detail ── */}
        {selectedFeature && (
          <FeatureDetail feature={selectedFeature} onClose={() => selectFeature(null)} />
        )}

        {/* ── Info bar ── */}
        <div className={styles.infoBar}>
          <Info size={16} weight="fill" style={{ color: p.primary, flexShrink: 0 }} />
          <span style={{ color: p.text2, fontSize: 12, lineHeight: '18px' }}>
            Spatial data is stored in PostGIS and served as GeoJSON. Toggle layers, search features,
            or enable Census Data to explore ABS 2016/2021 choropleth maps.
          </span>
        </div>
      </div>
      </div>
      <GeoChat
        messages={chatMessages}
        onSend={handleChatSend}
        loading={chatLoading}
        suggestions={chatSuggestions}
        onSuggestionClick={handleChatSend}
        onClearChat={() => {
          setChatMessages([]);
          setChatSuggestions([]);
          clearAll();
          setChoroplethEnabled(false);
          setBasemap('Street');
          const map = mapInstanceRef.current;
          if (map) map.setView([-37.78, 145.13], 13);
        }}
        isCollapsed={chatCollapsed}
        onToggleCollapse={() => setChatCollapsed(v => !v)}
      />
      </div>
    </div>
  );
}
